<template>
    <Card>
        <div class="twofa-card">
            <div class="twofa-card__information">
                <Heading
                    :labelTitle="$t.__('settings.StaffSettings.mfa.title')"
                    variant="h2"
                    :prefixColoredIcon="{ icon: 'lock', color: 'brand' }"
                />
                <div class="twofa-card__information-content typographies-paragraph-medium-regular">
                    <p>{{ $t.__('settings.StaffSettings.mfa.content.1') }}</p>
                    <p v-html="twoFAContentWithBold" />
                </div>
                <CompassLink
                    :label="$t.__('settings.StaffSettings.mfa.link')"
                    link="https://help.sellsy.com/fr/articles/6178777-fonctionnement-de-la-double-authentification"
                    target="_blank"
                    suffixIcon="external-link"
                />
            </div>
            <div class="twofa-card__action">
                <div
                    class="twofa-card__action-info typographies-paragraph-medium-regular"
                    :class="twoFAStatusClass"
                >
                    <CompassUser
                        v-if="activatorDetails"
                        size="extra-small"
                        :firstname="activatorDetails.forename"
                        :lastname="activatorDetails.name"
                    >
                        <CompassAvatar
                            :initials="getInitials(activatorDetails)"
                            :colorType="getAvatarColor(activatorDetails)"
                        />
                    </CompassUser>
                    <p v-html="twoFAStatusDescription" />
                    <CompassButton
                        v-if="twoFAStatus === TwoFAStatusEnum.SCHEDULED"
                        leftIcon="calendar-fill"
                        type="tertiary"
                        :label="$t.__('settings.StaffSettings.mfa.button.modify')"
                        @click="enable2FA(TwoFAActionsEnum.MODIFY)"
                    />
                </div>
                <CompassButton
                    v-if="
                        twoFAStatus !== TwoFAStatusEnum.ENABLED &&
                        twoFAStatus !== TwoFAStatusEnum.SCHEDULED
                    "
                    leftIcon="shield-check"
                    :label="$t.__('settings.StaffSettings.mfa.button.activate')"
                    @click="enable2FA(TwoFAActionsEnum.ACTIVATE)"
                />
                <CompassButton
                    v-else
                    :label="
                        twoFAStatus === TwoFAStatusEnum.SCHEDULED
                            ? $t.__('settings.StaffSettings.mfa.button.delete')
                            : $t.__('settings.StaffSettings.mfa.button.deactivate')
                    "
                    type="secondary"
                    @click="
                        enable2FA(
                            twoFAStatus === TwoFAStatusEnum.SCHEDULED
                                ? TwoFAActionsEnum.DELETE
                                : TwoFAActionsEnum.DEACTIVATE,
                        )
                    "
                />
            </div>
        </div>
    </Card>
    <Card class="staff-card">
        <div class="staff-card__header">
            <Heading
                :labelTitle="$t.__('settings.StaffSettings.staff.title')"
                variant="h2"
                :prefixColoredIcon="{ icon: 'profile', color: 'brand' }"
            />
            <CompassButton
                leftIcon="plus"
                :label="$t.__('settings.StaffSettings.staff.add_button')"
                :disabled="isAddStaffDisabled"
                @click="addStaff"
            />
        </div>
        <div class="staff-card__content typographies-paragraph-medium-regular">
            <p class="staff-card__content-support">
                <span>{{ $t.__('settings.StaffSettings.staff.content') }}</span>
                {{ ' ' }}
                <CompassPlainButton :label="$t.__('common.labels.chat')" @click="openChat" />.
            </p>
        </div>
        <CompassAlert
            v-if="data.corpInfo.nbAvailableStaff > 0"
            :description="availableStaffAlert"
            :showClose="false"
        />
        <CompassTable :isCheckable="false" class="staff-settings-table" size="medium">
            <CompassTableHead>
                <CompassTableHeadCell id="activeColumn" fixed="left">
                    {{ $t.__('settings.StaffSettings.staff.table.head.active') }}
                </CompassTableHeadCell>
                <CompassTableHeadCell id="nameColumn" fixed="left">
                    {{ $t.__('settings.StaffSettings.staff.table.head.name') }}
                </CompassTableHeadCell>
                <CompassTableHeadCell>
                    {{ $t.__('settings.StaffSettings.staff.table.head.profile') }}
                </CompassTableHeadCell>
                <CompassTableHeadCell>
                    {{ $t.__('settings.StaffSettings.staff.table.head.type') }}
                </CompassTableHeadCell>
                <CompassTableHeadCell>
                    {{ $t.__('settings.StaffSettings.staff.table.head.admin') }}
                </CompassTableHeadCell>
                <CompassTableHeadCell>
                    {{ $t.__('settings.StaffSettings.staff.table.head.2fa') }}
                </CompassTableHeadCell>
                <CompassTableHeadCell>
                    {{ $t.__('settings.StaffSettings.staff.table.head.decisionMaker') }}
                </CompassTableHeadCell>
                <CompassTableHeadCell id="actionColumn" fixed="right" />
            </CompassTableHead>
            <CompassTableBody>
                <CompassTableRow v-for="staff in staffList" :key="staff.id">
                    <CompassTableBodyCell idColumn="activeColumn">
                        <CompassSwitch
                            :modelValue="staff.status === 'ok'"
                            :disabled="isCurrentStaff(staff) || !isRegularStaff(staff)"
                            @change="toggleStaffStatus(staff)"
                        />
                    </CompassTableBodyCell>
                    <CompassTableBodyCell idColumn="nameColumn">
                        <div class="staff-settings-table__name-container">
                            <div class="staff-settings-table__name" @click="seeTheStaff(staff)">
                                {{ staff.forename }} {{ staff.name }}
                            </div>
                            <div
                                class="staff-settings-table__position typographies-paragraph-small-regular"
                            >
                                {{ staff.position }}
                            </div>
                        </div>
                    </CompassTableBodyCell>
                    <CompassTableBodyCell>
                        {{ staff.formatted_permissionprofile }}
                    </CompassTableBodyCell>
                    <CompassTableBodyCell>
                        <div
                            class="staff-settings-table__type typographies-paragraph-medium-regular"
                        >
                            <CompassColoredIcon
                                :icon="getFormatStaffType(staff).icon"
                                :color="getFormatStaffType(staff).color"
                            />
                            {{ getFormatStaffType(staff).label }}
                        </div>
                    </CompassTableBodyCell>
                    <CompassTableBodyCell>
                        <CompassIcon
                            v-if="staff.isAdmin"
                            class="staff-settings-table__check"
                            icon="success-circle-fill"
                        />
                    </CompassTableBodyCell>
                    <CompassTableBodyCell>
                        <CompassTag v-if="staff.twoFactorEnabled" color="green">
                            {{ $t.__('settings.StaffSettings.staff.table.body.active') }}
                        </CompassTag>
                        <CompassTag v-else color="gray">
                            {{ $t.__('settings.StaffSettings.staff.table.body.deactivated') }}
                        </CompassTag>
                    </CompassTableBodyCell>
                    <CompassTableBodyCell>
                        {{ staff.decisionMaker }}
                        <CompassSwitch
                            :modelValue="staff.isDecisionMaker"
                            :disabled="disableDecisionMakerSwitch(staff)"
                            @change="toggleStaffDecisionMaker(staff)"
                        />
                    </CompassTableBodyCell>
                    <CompassTableBodyCell idColumn="actionColumn">
                        <CompassMenu
                            customClass="staff-settings-table__actions-menu"
                            placement="bottom-end"
                        >
                            <CompassIconButton icon="action" />
                            <template #dropdown>
                                <CompassMenuItem
                                    :label="$t.__('settings.StaffSettings.staff.table.body.see')"
                                    @click="seeTheStaff(staff)"
                                />
                                <CompassMenuItem
                                    v-for="action in menuActions(staff)"
                                    :key="action.type"
                                    :label="action.label"
                                    @click="action.onClick()"
                                />
                            </template>
                        </CompassMenu>
                    </CompassTableBodyCell>
                </CompassTableRow>
            </CompassTableBody>
        </CompassTable>
    </Card>
</template>
<script setup>
import Card from 'vcomponents/common/card/Card.vue';
import Heading from 'vcomponents/common/Heading/Heading.vue';
import { computed, inject, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { goToUrl } from 'vueDir/helpers.js';
import { useStore } from 'vuex';
import Feature from 'Services/Feature';
import StaffApi from 'api/staff/staff';
import ErrorsHandler from 'Services/ErrorsHandler';
import { randAvatarColorFullname } from 'utils/avatar';

const props = defineProps({
    data: {
        type: Object,
        required: true,
    },
});

// Store
const store = useStore();

// Injected
const $t = inject('$t');
const $pushToast = inject('$pushToast');

// Refs
const twoFAKey = ref(0);
const has2FAEnabled = ref(props.data.corpInfo.twoFactorAuthentication.enabled);
const staffList = ref(props.data.staffList);
const twoFactorAuthentication = ref(props.data.corpInfo.twoFactorAuthentication);

// Consts
const TwoFAStatusEnum = Object.freeze({
    ENABLED: 'enabled',
    DISABLED: 'disabled',
    SCHEDULED: 'scheduled',
});
const TwoFAActionsEnum = Object.freeze({
    ACTIVATE: 'activate',
    MODIFY: 'modify',
    DEACTIVATE: 'deactivate',
    DELETE: 'delete',
});

const availableStaffAlert = computed(() => {
    return $t.__('settings.StaffSettings.staff.availableStaffAlert', {
        used: `<strong class="typographies-paragraph-medium-semibold">${props.data.corpInfo.nbStaffUsed}</strong>`,
        total: `<strong class="typographies-paragraph-medium-semibold">${props.data.corpInfo.nbAvailableStaff}</strong>`,
        lineBreak: '<br/>',
    });
});

// Computed
const twoFAStatus = computed(() => {
    if (has2FAEnabled.value) {
        return TwoFAStatusEnum.ENABLED;
    }
    if (twoFactorAuthentication.value.activation_scheduled_date) {
        return TwoFAStatusEnum.SCHEDULED;
    }
    return TwoFAStatusEnum.DISABLED;
});
const twoFAContentWithBold = computed(() => {
    return $t.__('settings.StaffSettings.mfa.content.2', {
        boldStart: '<strong class="typographies-paragraph-medium-semibold">',
        boldEnd: '</strong>',
    });
});
const twoFAStatusClass = computed(() => {
    switch (twoFAStatus.value) {
        case TwoFAStatusEnum.ENABLED:
            return 'twofa-card__action-info--is-enabled';
        case TwoFAStatusEnum.DISABLED:
            return 'twofa-card__action-info--is-disabled';
        case TwoFAStatusEnum.SCHEDULED:
            return 'twofa-card__action-info--is-scheduled';
        default:
            return '';
    }
});

const twoFAStatusDescription = computed(() => {
    if (twoFAStatus.value === TwoFAStatusEnum.DISABLED) {
        return $t.__('settings.StaffSettings.mfa.status.disabled');
    }

    if (
        (!twoFactorAuthentication.value.activation_date ||
            !twoFactorAuthentication.value.staff_id) &&
        twoFactorAuthentication.value.enabled
    ) {
        return $t.__('settings.StaffSettings.mfa.status.enabled_no_log');
    }

    const timeFormat = store.state.corp.timeformat === 'dmy' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
    const hourFormat = store.state.corp.hourformat === '12' ? 'hh:mm a' : 'HH:mm';
    const staffTimezone = store.state.user.timezone;

    switch (twoFAStatus.value) {
        case TwoFAStatusEnum.ENABLED: {
            const activation = moment.tz(
                twoFactorAuthentication.value.activation_date,
                staffTimezone,
            );
            return $t.__('settings.StaffSettings.mfa.status.enabled', {
                date: activation.format(timeFormat),
                time: activation.format(hourFormat),
                boldStart: '<strong class="typographies-paragraph-medium-semibold">',
                boldEnd: '</strong>',
            });
        }
        case TwoFAStatusEnum.SCHEDULED: {
            const scheduled = moment.tz(
                twoFactorAuthentication.value.activation_scheduled_date,
                staffTimezone,
            );
            return $t.__('settings.StaffSettings.mfa.status.scheduled', {
                date: scheduled.format(timeFormat),
                time: scheduled.format(hourFormat),
                boldStart: '<strong class="typographies-paragraph-medium-semibold">',
                boldEnd: '</strong>',
            });
        }
        default:
            return '';
    }
});
const activatorDetails = computed(() => {
    if (
        !twoFactorAuthentication.value.activation_date &&
        !twoFactorAuthentication.value.activation_scheduled_date
    ) {
        return;
    }
    return props.data.staffList.find(
        (staff) => Number(staff.id) === Number(twoFactorAuthentication.value.staff_id),
    );
});
const isAddStaffDisabled = computed(() => {
    const { corpInfo } = props.data;
    return (
        corpInfo.nbAvailableStaff > 0 &&
        props.data.isSam &&
        corpInfo.nbStaffUsed === corpInfo.nbAvailableStaff &&
        corpInfo.accountLevel !== 'gold'
    );
});

// Methods
const getFormatStaffType = (staff) => {
    if (Number(staff.ownerid) === 0) {
        return {
            color: 'purple',
            label: $t.__('settings.StaffSettings.staff.type.creator'),
            icon: 'user-hexagon',
        };
    }

    const typeMap = {
        accountant: {
            color: 'orange',
            label: $t.__('settings.StaffSettings.staff.type.accountant'),
            icon: 'pig-money',
        },
        sellsy: {
            color: 'gray',
            label: $t.__('settings.StaffSettings.staff.type.support'),
            icon: 'comment',
        },
        application: {
            color: 'gray',
            label: $t.__('settings.StaffSettings.staff.type.application'),
            icon: 'nav-catalogue',
        },
        temporary: {
            color: 'gray',
            label: $t.__('settings.StaffSettings.staff.type.temporary'),
            icon: 'clock',
        },
    };

    return (
        typeMap[staff.type] || {
            color: 'gray',
            label: $t.__('settings.StaffSettings.staff.type.staff'),
            icon: 'profile',
        }
    );
};
const enable2FA = (status) => {
    if (status === TwoFAActionsEnum.ACTIVATE || status === TwoFAActionsEnum.MODIFY) {
        store.dispatch('popupPanel/open', {
            key: 'staff2FAScheduleModal',
            props: {
                isEdition: status === TwoFAActionsEnum.MODIFY,
            },
        });
    } else {
        store.dispatch('popupPanel/open', {
            key: 'staff2FACancelModal',
            props: {
                isActive: status === TwoFAActionsEnum.DEACTIVATE,
            },
        });
    }
};
const addStaff = () => {
    const { corpInfo } = props.data;
    if (
        corpInfo.nbAvailableStaff > 0 &&
        props.data.isSam &&
        corpInfo.nbStaffUsed === corpInfo.nbAvailableStaff &&
        corpInfo.accountLevel === 'gold'
    ) {
        goToUrl('/subscription/cart');
        return;
    }

    store.dispatch('popupPanel/open', {
        key: 'staffForm',
        props: {
            isCorpSellsy: props.data.isCorpSellsy,
            staffid: false,
        },
    });
};
const openChat = () => {
    if (Feature.isEnabled('intercom')) {
        window.Intercom('show');
    }
};
const isCurrentStaff = (staff) => {
    return Number(staff.id) === Number(store.getters['user/getCurrentUserId']);
};
const isRegularStaff = (staff) => {
    return staff.type === 'regular';
};
const toggleStaffStatus = async (staff) => {
    try {
        await StaffApi.updateStaff({
            id: staff.id,
            is_active: staff.status !== 'ok',
        });
        staff.status = staff.status === 'ok' ? 'blocked' : 'ok';
        const decisionMakerAction = staff.availableActions?.find(
            (action) => action.type === 'isDecisionMaker',
        );
        if (decisionMakerAction) {
            decisionMakerAction.disable = staff.status !== 'ok';
        }
        $pushToast({
            text: $t.__('settings.StaffSettings.staff.status.success'),
            type: 'success',
        });
        nextTick(() => {
            disableDecisionMakerSwitch(staff);
        });
    } catch (error) {
        $pushToast({
            text: $t.__('settings.StaffSettings.staff.status.fail'),
            type: 'error',
        });
        ErrorsHandler.logException(error);
    }
};
const disableDecisionMakerSwitch = (staff) => {
    if (!staff || staff.status !== 'ok') {
        return true;
    }
    const hasAction = staff.availableActions?.find((action) => action.type === 'isDecisionMaker');

    return !hasAction || hasAction.disable;
};
const toggleStaffDecisionMaker = async (staff) => {
    window.sellsyapp.config.globalProperties.$ajax.put(
        `/staff/isDecisionMaker/${staff.id}`,
        {},
        { value: !staff.isDecisionMaker },
        () => {
            staff.isDecisionMaker = !staff.isDecisionMaker;
            $pushToast({
                text: $t.__('settings.StaffSettings.staff.isDecisionMaker.success'),
                type: 'success',
            });
        },
        (error) => {
            $pushToast({
                text: $t.__('settings.StaffSettings.staff.isDecisionMaker.fail'),
                type: 'error',
            });
            ErrorsHandler.logException(error);
        },
    );
};
const seeTheStaff = (staff) => {
    window.open(`/staff/${staff.id}`, '_blank');
};
const menuActions = (staff) => {
    const labelMap = {
        edit: $t.__('common.actions.edit'),
        delete: $t.__('common.actions.delete'),
        generate2FAToken: $t.__('settings.StaffSettings.staff.reauthentication'),
        setCorpOwner: $t.__('settings.StaffSettings.staff.setCorpOwner'),
    };
    const modalMap = {
        edit: {
            key: 'staffForm',
            props: (staff) => ({
                staffid: Number(staff.id),
                isCorpSellsy: props.data.isCorpSellsy,
            }),
        },
        delete: {
            key: 'staffConfirmDeleteModal',
            props: (staff) => ({
                staffId: staff.id,
            }),
        },
        generate2FAToken: {
            key: 'staffOneOffBackupCodeModal',
            props: (staff) => ({
                staff,
            }),
        },
        setCorpOwner: {
            key: 'confirmChangeOwnerModal',
            props: (staff) => ({
                staffId: Number(staff.id),
                name: staff.fullName,
            }),
        },
    };

    return staff.availableActions
        .filter((action) => action.type !== 'isDecisionMaker')
        .map((action) => ({
            type: action.type,
            label: labelMap[action.type] || action.type,
            onClick: () => {
                const modal = modalMap[action.type];
                if (modal) {
                    store.dispatch('popupPanel/open', {
                        key: modal.key,
                        props: modal.props(staff),
                    });
                }
            },
        }));
};
const getAvatarColor = (person) => {
    return randAvatarColorFullname(person.fullName);
};
const getInitials = (person) => {
    const initialFirstName = person.forename ? person.forename[0] : '';
    const initialLastName = person.name ? person.name[0] : '';
    return `${initialFirstName}${initialLastName}`;
};

// Lifecycle
const updateTwoFactorSettings = (data) => {
    twoFactorAuthentication.value = data;
    has2FAEnabled.value = data.enabled;
    twoFAKey.value += 1;
};

onMounted(() => {
    EventBus.on('staffSettings:2faUpdated', updateTwoFactorSettings);
    EventBus.on('sse:twofactor:settings:changed', updateTwoFactorSettings);
    EventBus.on('staffSettings:staffDeleted', (staffId) => {
        staffList.value = staffList.value.filter((staff) => staff.id !== staffId);
    });
    EventBus.on('staffSettings:staffListUpdated', (updatedList) => {
        staffList.value = updatedList;
    });
    EventBus.on('staffSettings:updateOwner', (updatedList) => {
        staffList.value = updatedList;
    });
});
onUnmounted(() => {
    EventBus.off('staffSettings:2faUpdated', updateTwoFactorSettings);
    EventBus.off('sse:twofactor:settings:changed', updateTwoFactorSettings);
    EventBus.off('staffSettings:staffDeleted');
    EventBus.off('staffSettings:staffListUpdated');
    EventBus.off('staffSettings:updateOwner');
});
</script>
<style lang="scss">
#compass-menu-container {
    .staff-settings-table__actions-menu {
        width: 180px;
        z-index: 1000;
    }
}
</style>
<style lang="scss" scoped>
.twofa-card {
    display: grid;
    gap: var(--spacing-3);
    grid-template-columns: repeat(12, 1fr);

    &__information {
        align-items: start;
        display: flex;
        flex-direction: column;
        gap: 0;
        grid-column: span 8;

        &-content {
            margin: 0 0 var(--spacing-3);
        }

        p {
            margin: 0;
        }
    }

    &__action {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3);
        grid-column: span 4;

        p {
            margin: 0;
        }

        &-info {
            border-radius: var(--spacing-1);
            padding: var(--spacing-3);

            &--is-disabled {
                background-color: var(--colors-common-background-subtle);
            }

            &--is-enabled {
                background-color: var(--colors-common-background-success-subtle);
            }

            &--is-scheduled {
                background-color: var(--colors-common-background-decorative-light-blue);
            }

            :deep(.compass-user) {
                margin: 0 0 var(--spacing-0);
            }

            :deep(.compass-button) {
                margin: var(--spacing-3) 0 0;
                width: 100%;
            }
        }
    }
}

.staff-card {
    &__header {
        align-items: start;
        display: flex;
        gap: var(--spacing-3);
        justify-content: space-between;
    }

    &__content {
        margin: 0 0 var(--spacing-3);

        p {
            margin: 0;
        }
    }
}

.staff-settings-table {
    &__type {
        align-items: center;
        display: flex;
        gap: var(--spacing-1);
    }

    &__check {
        color: var(--colors-common-icon-success);
    }

    &__name {
        cursor: pointer;
        max-width: 250px;
        white-space: normal;

        &-container {
            display: flex;
            flex-direction: column;
        }
    }

    &__position {
        color: var(--colors-common-text-subtle-default);
    }
}
</style>
